import { useEffect, useState } from 'react';
import { useUser } from './useUser';
import { useDebounce } from './useDebounce';
import Utils from '../utils/utils';
import { OperationSearchResult, searchOperationAgencyOfficials } from '../networkRequest/operationsService';

export type SearchResultOption = {
  id: string
  label: string
  searchResult: OperationSearchResult
};

const useSearchOfficials = ({ operationId }: {
  operationId: string;
}) => {
  const { user } = useUser();
  const [search, setSearch] = useState<string>('');
  const [searchResultOptions, setSearchResultOptions] = useState<SearchResultOption[]>([]);
  const [searchOfficialsError, setSearchOfficialsError] = useState<string | null>(null);

  const debouncedSearch = useDebounce((term: string) => {
    setSearch(term);
  }, 300);
  
  const handleSearch = (newValue: string) => {
    debouncedSearch(newValue);
  };

  // Converting each operation team search result to a list of search results 
  // for the operation officials that belong to that team.
  // Ensure that officials are not repeated.
  const parseOfficials = (results): SearchResultOption[] => {
    const _selectedOptions: SearchResultOption[] = [];
    const seenOfficialIds: any = {};
    
    for (const item of results) {
      if (item.searchResult.kind === 'operation_agency_official') {
        if (!seenOfficialIds[item.id]) {
          _selectedOptions.push(item);
          seenOfficialIds[item.id] = true;
        }
      }
      if (item.searchResult.kind === 'operation_team') {
        item.searchResult.team.officials.map((official) => {
          if (!seenOfficialIds[official.id]) {
            _selectedOptions.push({
              id: official.id,
              label: Utils.fullName(
                official.agency_official.first_name,
                official.agency_official.last_name
              ),
              searchResult: {
                kind: 'operation_agency_official',
                official: official
              }
            });
            seenOfficialIds[official.id] = true;
          }
        });
      }
    }
    return _selectedOptions;
  };

  useEffect(() => {
    if (!search.length) return;
    const getAgencyOfficials = async () => {
      const { data: searchResults, error } = await searchOperationAgencyOfficials({
        accessToken: user?.accessToken ?? '',
        operationId,
        search
      });

      if (searchResults) {
        const _searchResultOptions: SearchResultOption[] = searchResults.map((searchResult) => {
          if (searchResult.kind === 'operation_agency_official') {
            return {
              id: searchResult.official.id,
              label: Utils.fullName(
                searchResult.official.agency_official.first_name,
                searchResult.official.agency_official.last_name
              ),
              searchResult: searchResult
            };
          }
          else {
            return {
              id: searchResult.team.id,
              label: searchResult.team.name,
              searchResult: searchResult
            };
          }
        });
        setSearchResultOptions(_searchResultOptions);
      } else if (error) {
        setSearchOfficialsError('Search officials error');
      }
    };
    getAgencyOfficials();
  }, [search]);

  return {
    searchResultOptions,
    handleSearch,
    search,
    setSearch,
    searchOfficialsError,
    parseOfficials,
  }
}

export default useSearchOfficials;